import { formatDateTime } from "../../utils/applicationFormatters";
import { formatAmount } from "@/lib/utils/formatters";
import { SectionInfoIcon } from "@/components/ui/SectionInfoIcon";
import { getApiLogReadUrl } from "@/lib/utils/reportsNavigation";
import styles from "../../styles/applicationDetail.module.css";
import type { SignaturePaymentFailedAttemptsSectionProps } from "../../types";

/** Angular Failed Payment Attempts: amount pipe when truthy, else `$0`. */
function formatFailedAttemptTotal(amount: unknown): string {
  if (amount === null || amount === undefined || amount === "" || amount === 0 || amount === "0") {
    return "$0";
  }
  return formatAmount(amount as string | number);
}

export function SignaturePaymentFailedAttemptsSection({
  tab,
}: SignaturePaymentFailedAttemptsSectionProps) {
  const { failedApiLogs } = tab;

  if (failedApiLogs.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.section}
      data-testid="application-signature-payment-tab-section-failed-payment-attempts"
    >
      <h2 className={styles.sectionTitle}>Failed Payment Attempts</h2>
      <div className={styles.tableResponsive}>
        <table
          className={`${styles.table} ${styles.paymentLogTable} ${styles.failedPaymentAttemptsTable}`}
          data-testid="application-signature-payment-tab-failed-payment-attempts-table"
        >
          <thead>
            <tr>
              <th>Type</th>
              <th>Method</th>
              <th>Card Last 4</th>
              <th>Total</th>
              <th>Log ID</th>
              <th>Transaction</th>
              <th>Date / Time</th>
              <th>Status</th>
              <th>Error</th>
              <th>Attempted By</th>
            </tr>
          </thead>
          <tbody>
            {failedApiLogs.map((log: Record<string, unknown>, index: number) => {
              const logId = log.log_id;
              const logIdStr =
                logId !== undefined && logId !== null ? String(logId) : "";
              const apiLogHref = logIdStr !== "" ? getApiLogReadUrl(logIdStr) : "";
              const errTitleRaw =
                typeof log.error_message_title === "string"
                  ? log.error_message_title
                  : log.error_message_title != null
                    ? String(log.error_message_title)
                    : "";
              const errTitle = errTitleRaw.trim();
              const errBody =
                typeof log.error_message === "string"
                  ? log.error_message
                  : log.error_message != null
                    ? String(log.error_message)
                    : "";

              return (
                <tr key={index}>
                  <td>
                    {log.payment_type != null ? String(log.payment_type) : ""}
                  </td>
                  <td>
                    {log.payment_source != null
                      ? String(log.payment_source)
                      : ""}
                  </td>
                  <td>
                    {log.card_last != null ? String(log.card_last) : ""}
                  </td>
                  <td className={styles.paymentLogAmountCell}>{formatFailedAttemptTotal(log.amount)}</td>
                  <td>
                    {apiLogHref ? (
                      <a
                        href={apiLogHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.link}
                      >
                        {logIdStr}
                      </a>
                    ) : (
                      logIdStr
                    )}
                  </td>
                  <td>
                    {log.transaction_no != null
                      ? String(log.transaction_no)
                      : ""}
                  </td>
                  <td>
                    {formatDateTime(
                      log.created_at as string | number | Date | null | undefined,
                    )}
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${styles.statusFailed}`}
                    >
                      Declined
                    </span>
                  </td>
                  <td className={styles.failedPaymentAttemptsErrorCell}>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span>{errBody}</span>
                      {errTitle ? (
                        <SectionInfoIcon
                          align="start"
                          maxWidth={360}
                          iconSize={16}
                          isLastRow={index === failedApiLogs.length - 1}
                          aria-label={`Payment failure reason: ${errTitle}`}
                          clampPanelHeight={false}
                          className="shrink-0"
                          contentClassName="px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap"
                          content={errTitle}
                          data-testid={`application-signature-payment-tab-failed-payment-reason-${index}`}
                        />
                      ) : null}
                    </div>
                  </td>
                  <td>
                    {log.name_on_card != null ? String(log.name_on_card) : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
